import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, DollarSign, Calculator, UserCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import DataTable from '../../components/DataTable';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge } from '../../components/UI';
import ConfirmModal from '../../components/ConfirmModal';
import Breadcrumb from '../../components/Breadcrumb';
import FundingSourceModal from '../../components/FundingSourceModal';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { useCRUD } from '../../hooks/useCRUD';
import { employeeService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import toast from 'react-hot-toast';

const POSITIONS = ['Pharmacist','Cashier','Manager','Stock Keeper','Delivery','Accountant','Receptionist','Security','Cleaner','Other'];

export default function EmployeesPage() {
  const crud = useCRUD(employeeService, 'Employee');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const [salaryTarget, setSalaryTarget] = useState(null);
  const [salaryFundingOpen, setSFOpen] = useState(false);
  const [pendingSalaryEmp,  setPSEmp]  = useState(null);
  const [fundingOpen,   setFundingOpen]   = useState(false);
  const [fundingLabel,  setFundingLabel]   = useState('Pharmacy Cash');
  const [salaryHistory, setSalaryHistory]= useState([]);
  const [calculation, setCalc]          = useState(null);
  const [payForm, setPayForm]           = useState({ month: '', overtime: 0, bonus: 0, deductions: 0, method: 'cash', reference: '', notes: '', fundingSource: 'pharmacy_cash' });
  const [paying, setPaying]             = useState(false);
  const [calcLoading, setCalcLoading]   = useState(false);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm();

  useEffect(() => { crud.fetchData(); }, [crud.page, crud.search]); // eslint-disable-line
  useEffect(() => {
    if (crud.editItem) reset({ ...crud.editItem, hireDate: crud.editItem.hireDate?.split('T')[0] });
    else reset({ name:'', position:'', hireDate:'', baseSalary:'', phone:'', email:'', gender:'', currency:'AFN' });
  }, [crud.editItem]); // eslint-disable-line

  const openSalary = async (emp) => {
    setSalaryTarget(emp);
    setCalc(null);
    setPayForm({ month: new Date().toISOString().slice(0,7), overtime:0, bonus:0, deductions:0, method:'cash', reference:'', notes:'' });
    try {
      const res = await employeeService.getSalaryHistory(emp._id);
      setSalaryHistory(res.data.data || []);
    } catch {}
  };

  const handleCalculate = async () => {
    if (!salaryTarget) return;
    setCalcLoading(true);
    try {
      const res = await employeeService.calculateSalary(salaryTarget._id, {
        overtime: payForm.overtime, bonus: payForm.bonus, deductions: payForm.deductions
      });
      setCalc(res.data.data);
    } catch (e) { toast.error('Calculation failed'); }
    finally { setCalcLoading(false); }
  };

  const handlePaySalary = async () => {
    if (!salaryTarget || !payForm.month) return toast.error('Month is required');
    setPaying(true);
    try {
      await employeeService.paySalary(salaryTarget._id, payForm);
      toast.success(`Salary paid for ${salaryTarget.name}`);
      setCalc(null);
      const res = await employeeService.getSalaryHistory(salaryTarget._id);
      setSalaryHistory(res.data.data || []);
      crud.fetchData();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setPaying(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await employeeService.delete(deleteTarget._id);
      toast.success('Employee deactivated');
      crud.fetchData();
    } catch (e) { toast.error('Failed'); }
    finally { setDeleting(false); setDeleteTarget(null); }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true,
      render: (v, row) => <div><p className="font-medium text-gray-900">{v}</p><p className="text-xs text-gray-400">{row.employeeId}</p></div>
    },
    { key: 'position',  label: 'Position' },
    { key: 'phone',     label: 'Phone' },
    { key: 'hireDate',  label: 'Hire Date',   render: (v) => toJalali(v) },
    { key: 'baseSalary',label: 'Base Salary', render: (v, row) => `${formatCurrency(v)} ${row.currency||'AFN'}` },
    { key: 'isActive',  label: 'Status', render: (v) => <Badge color={v?'green':'gray'}>{v?'Active':'Inactive'}</Badge> },
  ];

  return (
    <div>
      <Breadcrumb/>
      <PageHeader title="Employees" subtitle="Manage staff and salary payments"
        actions={<Button onClick={crud.openCreate}><Plus size={16}/> Add Employee</Button>}/>

      <div className="hidden md:block">
        <DataTable columns={columns} data={crud.data} loading={crud.loading}
          totalItems={crud.total} page={crud.page} pageSize={20}
          onPageChange={crud.setPage} onSearch={crud.setSearch}
          title="Employees" exportFilename="employees"
          actions={(row) => (
            <>
              <button onClick={() => { setPSEmp(row); setSFOpen(true); }} className="p-1.5 rounded hover:bg-green-50 text-gray-400 hover:text-green-600" title="Salary"><DollarSign size={14}/></button>
              <button onClick={() => crud.openEdit(row)} className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="Edit"><Pencil size={14}/></button>
              <button onClick={() => setDeleteTarget(row)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Delete"><Trash2 size={14}/></button>
            </>
          )}
        />
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {crud.data.map(row => (
          <div key={row._id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <div><p className="font-semibold text-gray-900">{row.name}</p><p className="text-xs text-gray-400">{row.position}</p></div>
              <Badge color={row.isActive?'green':'gray'}>{row.isActive?'Active':'Inactive'}</Badge>
            </div>
            <p className="text-sm text-gray-600 mb-3">Salary: <strong>{formatCurrency(row.baseSalary)} {row.currency}</strong></p>
            <div className="flex gap-2 pt-2 border-t border-gray-50">
              <button onClick={()=>{ setPSEmp(row); setSFOpen(true); }} className="flex-1 text-xs py-1.5 rounded bg-green-50 text-green-700 font-medium">Salary</button>
              <button onClick={()=>crud.openEdit(row)} className="flex-1 text-xs py-1.5 rounded bg-blue-50 text-blue-700 font-medium">Edit</button>
              <button onClick={()=>setDeleteTarget(row)} className="flex-1 text-xs py-1.5 rounded bg-red-50 text-red-700 font-medium">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={crud.modalOpen} onClose={crud.closeModal}
        title={crud.editItem ? 'Edit Employee' : 'Add Employee'} size="lg">
        <form onSubmit={handleSubmit(crud.handleSave)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Full Name" required error={errors.name?.message}>
              <Input {...register('name',{required:'Required'})} placeholder="Full name"/>
            </FormField>
            <FormField label="Position" required error={errors.position?.message}>
              <Select {...register('position',{required:'Required'})}>
                <option value="">Select position</option>
                {POSITIONS.map(p=><option key={p} value={p}>{p}</option>)}
              </Select>
            </FormField>
            <FormField label="Phone">
              <Input {...register('phone')} placeholder="+93 700 000 000"/>
            </FormField>
            <FormField label="Email">
              <Input type="email" {...register('email')} placeholder="email@example.com"/>
            </FormField>
            <FormField label="Hire Date" required error={errors.hireDate?.message}>
              <JalaliDatePicker value={watch('hireDate')||''} onChange={v=>setValue('hireDate',v)} placeholder="Hire date" className="w-full"/>
            </FormField>
            <FormField label="Gender">
              <Select {...register('gender')}>
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </Select>
            </FormField>
            <FormField label="Base Salary" required error={errors.baseSalary?.message}>
              <Input type="number" min="0" {...register('baseSalary',{required:'Required'})} placeholder="0"/>
            </FormField>
            <FormField label="Currency">
              <Select {...register('currency')}>
                <option value="AFN">AFN</option>
                <option value="USD">USD</option>
                <option value="PKR">PKR</option>
              </Select>
            </FormField>
            <FormField label="Employee ID">
              <Input {...register('employeeId')} placeholder="EMP-001"/>
            </FormField>
            <FormField label="Department">
              <Input {...register('department')} placeholder="Department"/>
            </FormField>
            <FormField label="Bank Name">
              <Input {...register('bankName')} placeholder="Bank name"/>
            </FormField>
            <FormField label="Bank Account">
              <Input {...register('bankAccount')} placeholder="Account number"/>
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Emergency Contact">
              <Input {...register('emergencyContact')} placeholder="Name"/>
            </FormField>
            <FormField label="Emergency Phone">
              <Input {...register('emergencyPhone')} placeholder="Phone"/>
            </FormField>
          </div>
          <FormField label="Notes">
            <textarea {...register('notes')} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Notes"/>
          </FormField>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={crud.closeModal}>Cancel</Button>
            <Button type="submit" loading={crud.saving}>{crud.editItem?'Update':'Save'}</Button>
          </div>
        </form>
      </Modal>

      {/* Salary Funding Modal */}
      <FundingSourceModal
        isOpen={salaryFundingOpen}
        onClose={()=>setSFOpen(false)}
        title="Select Funding Source for Salary Payment"
        onSelect={(src)=>{
          setSFOpen(false);
          openSalary(pendingSalaryEmp);
          setPayForm(p=>({...p, fundingSource: src.key}));
        }}
      />

      {/* Salary Modal */}
      <Modal isOpen={!!salaryTarget} onClose={()=>setSalaryTarget(null)}
        title={`Salary — ${salaryTarget?.name}`} size="lg">
        {salaryTarget && (
          <div className="space-y-4">
            {/* Employee info */}
            <div className="bg-gray-50 rounded-xl p-4 flex justify-between items-center">
              <div>
                <p className="font-semibold text-gray-800">{salaryTarget.name}</p>
                <p className="text-xs text-gray-500">{salaryTarget.position}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Base Salary</p>
                <p className="text-xl font-bold text-blue-700">{formatCurrency(salaryTarget.baseSalary)} {salaryTarget.currency}</p>
              </div>
            </div>

            {/* Pay form */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Pay Salary</p>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Month (YYYY-MM)" required>
                  <Input type="month" value={payForm.month} onChange={e=>setPayForm({...payForm,month:e.target.value})}/>
                </FormField>
                <FormField label="Overtime (hours)">
                  <Input type="number" min="0" value={payForm.overtime} onChange={e=>setPayForm({...payForm,overtime:e.target.value})}/>
                </FormField>
                <FormField label="Bonus (AFN)">
                  <Input type="number" min="0" value={payForm.bonus} onChange={e=>setPayForm({...payForm,bonus:e.target.value})}/>
                </FormField>
                <FormField label="Deductions (AFN)">
                  <Input type="number" min="0" value={payForm.deductions} onChange={e=>setPayForm({...payForm,deductions:e.target.value})}/>
                </FormField>
                <FormField label="Payment Method">
                  <Select value={payForm.method} onChange={e=>setPayForm({...payForm,method:e.target.value})}>
                    <option value="cash">Cash</option>
                    <option value="bank">Bank</option>
                    <option value="mobile">Mobile</option>
                  </Select>
                </FormField>
                <FormField label="Reference">
                  <Input value={payForm.reference} onChange={e=>setPayForm({...payForm,reference:e.target.value})} placeholder="Ref no."/>
                </FormField>
              </div>
              <div className="flex gap-2 items-start">
                <Button variant="outline" onClick={handleCalculate} loading={calcLoading} size="sm">
                  <Calculator size={14}/> Calculate
                </Button>
                {calculation && (
                  <div className="flex-1 bg-white rounded-lg p-3 text-xs space-y-1 border border-gray-200">
                    <div className="flex justify-between"><span className="text-gray-500">Base</span><span>{formatCurrency(calculation.baseSalary)}</span></div>
                    {calculation.overtimeAmount>0&&<div className="flex justify-between"><span className="text-gray-500">Overtime ({calculation.overtimeHours}h)</span><span className="text-green-600">+{formatCurrency(calculation.overtimeAmount)}</span></div>}
                    {calculation.bonus>0&&<div className="flex justify-between"><span className="text-gray-500">Bonus</span><span className="text-green-600">+{formatCurrency(calculation.bonus)}</span></div>}
                    {calculation.deductions>0&&<div className="flex justify-between"><span className="text-gray-500">Deductions</span><span className="text-red-500">-{formatCurrency(calculation.deductions)}</span></div>}
                    <div className="flex justify-between font-bold border-t pt-1"><span>Net Salary</span><span className="text-blue-700">{formatCurrency(calculation.netSalary)}</span></div>
                  </div>
                )}
              </div>
              <FormField label="Funded By">
                <Select value={payForm.fundingSource||'pharmacy_cash'} onChange={e=>setPayForm({...payForm,fundingSource:e.target.value})}>
                  <option value="pharmacy_cash">Pharmacy Cash</option>
                </Select>
              </FormField>
              <FormField label="Notes">
                <Input value={payForm.notes} onChange={e=>setPayForm({...payForm,notes:e.target.value})} placeholder="Notes"/>
              </FormField>
              <Button onClick={handlePaySalary} loading={paying} className="w-full bg-green-600 hover:bg-green-700 text-white">
                <DollarSign size={15}/> Pay Salary
              </Button>
            </div>

            {/* Salary history */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Salary History</p>
              {salaryHistory.length===0 ? <p className="text-sm text-gray-400 text-center py-4">No payments yet</p> : (
                <div className="border border-gray-100 rounded-lg overflow-hidden max-h-52 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0"><tr>
                      <th className="text-left px-3 py-2 font-semibold text-gray-400">Month</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-400">Base</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-400">Overtime</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-400">Bonus</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-400">Deductions</th>
                      <th className="text-right px-3 py-2 font-semibold text-green-600">Net</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {salaryHistory.map(p=>(
                        <tr key={p._id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-gray-700">{p.month}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(p.baseSalary)}</td>
                          <td className="px-3 py-2 text-right text-green-600">{p.overtime>0?`+${formatCurrency(p.overtime)}`:'-'}</td>
                          <td className="px-3 py-2 text-right text-green-600">{p.bonus>0?`+${formatCurrency(p.bonus)}`:'-'}</td>
                          <td className="px-3 py-2 text-right text-red-500">{p.deductions>0?`-${formatCurrency(p.deductions)}`:'-'}</td>
                          <td className="px-3 py-2 text-right font-bold text-blue-700">{formatCurrency(p.netSalary)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal isOpen={!!deleteTarget} onClose={()=>setDeleteTarget(null)}
        onConfirm={handleDelete} loading={deleting}
        title="Deactivate Employee"
        message={`Deactivate "${deleteTarget?.name}"?`}/>

    </div>
  );
}
